import classes from './Cars.module.css';
import { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

// IMPORT HOOKS, CONTEXTS AND FUNCTIONS
import dateformat from 'dateformat';
import { useBrandFind } from '../../hooks/useFinders';
import CompareContext from '../../store/compare-context';
import { getApiUrl } from '../../config/api';

// IMPORT OWN COMPONENTS
import PriceInfo from '../UI/PriceInfo';

// IMPORT MUI COMPONENTS
import { ListItem, ListItemText } from '@mui/material';
import {
  faCalendar,
  faExclamation,
  faHammer,
  faRoad,
} from '@fortawesome/free-solid-svg-icons';

// IMPORT REACT SWIPER COMPONENTS
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';
import '../UI/swiperStyle.css';
import { Mousewheel, Scrollbar, Navigation, Keyboard } from 'swiper';

// IMPORT FONTAWESOME ICONS
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGear,
  faDroplet,
  faBrush,
  faGauge,
  faHeart,
} from '@fortawesome/free-solid-svg-icons';
import Animation from '../UI/Animation';
import AuthContext from '../../store/auth-context';
import { globals } from '../../data/global-vars';
import dayjs from 'dayjs';
import Button from '../UI/Button';

// HTML TEXT PARSER
const parse = require('html-react-parser');

// for swiper
const mousewheelOptions = {
  releaseOnEdges: true,
  forceToAxis: true,
  thresholdTime: 200,
  thresholdDelta: 20,
};

export function CarPreview(props) {
  const data = props.data;
  const params = useParams();
  const brand = params.brand;
  const history = useHistory();
  const brandObjectPrev = useBrandFind(brand);
  const brandObject = props.brandObject ? props.brandObject : brandObjectPrev;
  const compareCtx = useContext(CompareContext);
  let compare = compareCtx.cars.includes(data.carzillaId);
  const authCtx = useContext(AuthContext);

  const c = data;
  const dOp = props.displayOptions;
  const cons = data.consumptionAndEnvironment;
  const d = data.importantDetails;
  const [loadPicAmount, setLoadPicAmount] = useState(
    c.pictureCount > 4 ? 4 : c.pictureCount
  );

  let brandUrl = brandObject.url;

  if (c.branch === 'moenchhof') brandUrl = globals.BRANDS.SKODA.url;
  else if (c.branch === 'seyfarth_nw' || c.branch === 'seyfarth_gw')
    brandUrl = globals.BRANDS.VW.url;
  else if (c.brand === globals.BRANDS.AUDI.carzillaBrand)
    brandUrl = globals.BRANDS.AUDI.url;
  else if (c.brand === globals.BRANDS.VW_NFZ.carzillaBrand)
    brandUrl = globals.BRANDS.VW_NFZ.url;

  const lines = [
    {
      key: 'nextHU',
      icon: faExclamation,
      text: d.nextHU && dateformat(new Date(d.nextHU), 'mm/yyyy'),
    },
    {
      key: 'yearOfConstruction',
      icon: faHammer,
      text: d.yearOfConstruction,
    },
  ].filter((obj) => obj.text !== null);

  const consumptions = [
    {
      case: '<b>NEFZ:</b> Kraftstoffverbrauch innerorts',
      value: !cons.consumptionUrban
        ? null
        : cons.consumptionUrban + ' L/100km*',
    },
    {
      case: '<b>NEFZ:</b> Kraftstoffverbrauch außerorts',
      value: !cons.consumptionExtraUrban
        ? null
        : cons.consumptionExtraUrban + ' L/100km*',
    },
    {
      case: '<b>NEFZ:</b> Kraftstoffverbrauch kombiniert',
      value: !cons.consumptionCombined
        ? null
        : cons.consumptionCombined + ' L/100km*',
    },
    {
      case: '<b>NEFZ:</b> Stromverbrauch (kombiniert)',
      value: !cons.electricConsumptionCombined
        ? null
        : cons.electricConsumptionCombined + ' kWh/100km',
    },
    {
      case: '<b>NEFZ:</b> CO2-Emissionen kombiniert',
      value: !cons.co2Emission ? null : cons.co2Emission + ' g/km*',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (kombiniert)',
      value: !cons.consumptionWLTPCombined
        ? null
        : cons.consumptionWLTPCombined + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (langsam)',
      value: !cons.WLTPSlow ? null : cons.WLTPSlow + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (mittel)',
      value: !cons.WLTPMedium ? null : cons.WLTPMedium + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (schnell)',
      value: !cons.WLTPFast ? null : cons.WLTPFast + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (sehr schnell)',
      value: !cons.WLTPVeryFast ? null : cons.WLTPVeryFast + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Stromverbrauch (kombiniert)',
      value: !cons.electricConsumptionCombinedWLTP
        ? null
        : cons.electricConsumptionCombinedWLTP + ' kWh/100km',
    },
    {
      case: '<b>WLTP:</b> CO2-Emissionen',
      value: !cons.emissionWLTP ? null : cons.emissionWLTP + ' g/km',
    },
    {
      case: 'CO2-Effizienzklasse',
      value: !cons.co2Efficiency ? null : cons.co2Efficiency + '*',
    },
    {
      case: 'Emissionsklasse',
      value: cons.emissionClass,
    },
  ].filter((obj) => obj.value !== null);

  return (
    <Animation dir="left">
      <div
        className="w-full text-left grid grid-cols-12 cursor-pointer"
        onClick={() => {
          window.scrollTo(0, 0);
          history.push(`/${brandUrl}/details/${c.carzillaId}`);
        }}
      >
        {!props.noHeader && (
          <div className="relative col-span-12">
            {authCtx.isLoggedIn && (
              <span className="adminInfo">
                Nr.: {props.index} (ID: {data.carzillaId})
              </span>
            )}
            <h2 className={`text-3xl -mb-2 text-primary`}>
              {c.brand + ' ' + c.model}
            </h2>
            <h3 className="mb-2">{d.modelYear && d.modelYear}</h3>
            <FontAwesomeIcon
              icon={faHeart}
              onClick={(e) => {
                compareCtx.toggleCars(c.carzillaId);
                e.stopPropagation();
              }}
              className={`${classes.compareIcon} ${compare && classes.active} ${
                compare ? 'text-primary' : 'var(--text-color)'
              } cursor-pointer`}
            />
          </div>
        )}
        <div
          className="col-span-12 sm:col-span-6 md:col-span-12 flex justify-center items-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Swiper
            slidesPerView={1}
            grabCursor={true}
            mousewheel={mousewheelOptions}
            modules={[Mousewheel, Navigation, Scrollbar, Keyboard]}
            centerInsufficientSlides={true}
            navigation
            scrollbar={{ draggable: true }}
            // only load 4 at first then load more if user scrolls
            onSlideChange={(e) => {
              if (e.activeIndex + 1 === loadPicAmount)
                setLoadPicAmount(c.pictureCount);
            }}
            keyboard={{
              enabled: props.keyboardScroll ? true : false,
            }}
          >
            {Array.from({ length: loadPicAmount }).map((v, i) => {
              return (
                <SwiperSlide
                  key={i}
                  className="mb-3 px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <img
                    onClick={() => {
                      history.push(`/${brandUrl}/details/${c.carzillaId}`, {
                        scroll: true,
                      });
                    }}
                    className={`select-none rounded aspect-[1.333] w-full`}
                    alt={
                      'Bild des Neuwagens ' + c.model + ' der Firma ' + c.brand
                    }
                    loading={i >= 1 ? 'lazy' : 'eager'}
                    src={getApiUrl(
                      `/images/cars/${c.carzillaId}_${String(i + 1).padStart(
                        2,
                        '0'
                      )}.jpg`
                    )}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <div className="col-span-12 sm:col-span-6 md:col-span-12 grid grid-cols-12 gap-2 my-4 px-2">
          {(!dOp.kW || dOp.kW[0]) && d.powerKw && (
            <div
              className="col-span-3 rounded  p-1 aspect-square flex flex-col justify-center items-center"
              title="Leistung in Kilowatt"
            >
              <span className="text-2xl text-primary">
                {Math.round(d.powerKw).toFixed(0)}
              </span>
              <span className="text-sm">kW</span>
            </div>
          )}
          {(!dOp.kW || dOp.kW[0]) && d.powerPS && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Leistung in PS"
            >
              <span className="text-2xl text-primary">
                {Math.round(d.powerPS).toFixed(0)}
              </span>
              <span className="text-sm">PS</span>
            </div>
          )}
          {(!dOp.firstRegistration || dOp.firstRegistration[0]) &&
            d.firstRegistration && (
              <div
                className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
                title="Monat und Jahr der Erstzulassung"
              >
                <span className="text-2xl text-primary">
                  {dayjs(d.firstRegistration).format('M/YY')}
                </span>
                <span className="text-sm">EZ</span>
              </div>
            )}
          {(!dOp.seatCount || dOp.seatCount[0]) && d.seatCount && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Anzahl der Sitze"
            >
              <span className="text-2xl text-primary">{d.seatCount}</span>
              <span className="text-sm">Sitze</span>
            </div>
          )}
          {(!dOp.km || dOp.km[0]) && d.kilometers && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Kilometerstand"
            >
              <FontAwesomeIcon icon={faRoad} className="mr-4" />
              <span className="text-lg">
                {Math.round(d.kilometers).toLocaleString('de-DE')} km
              </span>
            </div>
          )}
          {(!dOp.color || dOp.color[0]) && d.baseColor && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Grundfarbe"
            >
              <FontAwesomeIcon icon={faBrush} className="mr-4" />
              <span className="text-lg">{d.baseColor}</span>
            </div>
          )}
          {(!dOp.transmissionType || dOp.transmissionType[0]) &&
            d.transmissionType && (
              <div
                className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
                title="Getriebe"
              >
                <FontAwesomeIcon icon={faGear} className="mr-4" />
                <span className="text-lg">{d.transmissionType}</span>
              </div>
            )}
          {(!dOp.enkvFuelType || dOp.enkvFuelType[0]) && d.fuelType && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Treibstoff"
            >
              <FontAwesomeIcon icon={faDroplet} className="mr-4" />
              <span className="text-lg">{d.fuelType}</span>
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-6 flex flex-col justify-center items-start">
          {lines.map((line, i) => {
            if (
              !props.displayOptions ||
              props.displayOptions[line.key][0] === true
            )
              return (
                <div key={i}>
                  <ListItem key={i} sx={{ padding: '10px 5px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '20px',
                        margin: '0 10px',
                      }}
                    >
                      <FontAwesomeIcon icon={line.icon} />
                    </div>
                    <ListItemText
                      className={classes.listItemText}
                      primary={line.text}
                    />
                  </ListItem>
                </div>
              );
          })}
        </div>
        <div
          className="col-span-12 my-5 px-4"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <PriceInfo
            total={d.price}
            financing={c.financingProposal.monthlyRate}
            priceFeature="financing"
            id={data.carzillaId}
            noAU={props.noAU}
          />
        </div>
        <div className="col-span-12 leading-3">
          {consumptions.map((c, i) => {
            return (
              <span key={i} className="adminInfo">
                <span>{parse(c.case)}:</span>
                <span>{' ' + c.value}</span>
                <span>{i !== consumptions.length - 1 && ', '}</span>
              </span>
            );
          })}
        </div>
        <span className="col-span-12 adminInfo">(ID: {data.carzillaId})</span>
      </div>
    </Animation>
  );
}
export function CarPreviewModal(props) {
  const data = props.data;
  const params = useParams();
  const brand = params.brand;
  const history = useHistory();
  const brandObjectPrev = useBrandFind(brand);
  const brandObject = props.brandObject ? props.brandObject : brandObjectPrev;
  const compareCtx = useContext(CompareContext);
  let compare = compareCtx.cars.includes(data.carzillaId);

  const c = data;
  const cons = data.consumptionAndEnvironment;
  const d = data.importantDetails;
  const [loadPicAmount, setLoadPicAmount] = useState(
    c.pictureCount > 4 ? 4 : c.pictureCount
  );

  let brandUrl = brandObject.url;
  if (c.branch === 'moenchhof') brandUrl = globals.BRANDS.SKODA.url;
  else if (c.branch === 'seyfarth_nw' || c.branch === 'seyfarth_gw')
    brandUrl = globals.BRANDS.VW.url;
  else if (c.brand === globals.BRANDS.AUDI.carzillaBrand)
    brandUrl = globals.BRANDS.AUDI.url;
  else if (c.brand === globals.BRANDS.VW_NFZ.carzillaBrand)
    brandUrl = globals.BRANDS.VW_NFZ.url;

  const consumptions = [
    {
      case: '<b>NEFZ:</b> Kraftstoffverbrauch innerorts',
      value: !cons.consumptionUrban
        ? null
        : cons.consumptionUrban + ' L/100km*',
    },
    {
      case: '<b>NEFZ:</b> Kraftstoffverbrauch außerorts',
      value: !cons.consumptionExtraUrban
        ? null
        : cons.consumptionExtraUrban + ' L/100km*',
    },
    {
      case: '<b>NEFZ:</b> Kraftstoffverbrauch kombiniert',
      value: !cons.consumptionCombined
        ? null
        : cons.consumptionCombined + ' L/100km*',
    },
    {
      case: '<b>NEFZ:</b> Stromverbrauch (kombiniert)',
      value: !cons.electricConsumptionCombined
        ? null
        : cons.electricConsumptionCombined + ' kWh/100km',
    },
    {
      case: '<b>NEFZ:</b> CO2-Emissionen kombiniert',
      value: !cons.co2Emission ? null : cons.co2Emission + ' g/km*',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (kombiniert)',
      value: !cons.consumptionWLTPCombined
        ? null
        : cons.consumptionWLTPCombined + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (langsam)',
      value: !cons.WLTPSlow ? null : cons.WLTPSlow + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (mittel)',
      value: !cons.WLTPMedium ? null : cons.WLTPMedium + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (schnell)',
      value: !cons.WLTPFast ? null : cons.WLTPFast + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Kraftstoffverbrauch (sehr schnell)',
      value: !cons.WLTPVeryFast ? null : cons.WLTPVeryFast + ' L/100km',
    },
    {
      case: '<b>WLTP:</b> Stromverbrauch (kombiniert)',
      value: !cons.electricConsumptionCombinedWLTP
        ? null
        : cons.electricConsumptionCombinedWLTP + ' kWh/100km',
    },
    {
      case: '<b>WLTP:</b> CO2-Emissionen',
      value: !cons.emissionWLTP ? null : cons.emissionWLTP + ' g/km',
    },
    {
      case: 'CO2-Effizienzklasse',
      value: !cons.co2Efficiency ? null : cons.co2Efficiency + '*',
    },
    {
      case: 'Emissionsklasse',
      value: cons.emissionClass,
    },
  ].filter((obj) => obj.value !== null);

  return (
    <Animation dir="left">
      <div
        className="w-full text-left grid grid-cols-12 cursor-pointer pb-4"
        onClick={() => {
          window.scrollTo(0, 0);
          history.push(`/${brandUrl}/details/${c.carzillaId}`);
        }}
      >
        <div
          className="col-span-12 md:col-span-6 flex justify-center items-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Swiper
            slidesPerView={1}
            grabCursor={true}
            mousewheel={mousewheelOptions}
            modules={[Mousewheel, Navigation, Scrollbar, Keyboard]}
            centerInsufficientSlides={true}
            navigation
            scrollbar={{ draggable: true }}
            // only load 4 at first then load more if user scrolls
            onSlideChange={(e) => {
              if (e.activeIndex + 1 === loadPicAmount)
                setLoadPicAmount(c.pictureCount);
            }}
            keyboard={{
              enabled: props.keyboardScroll ? true : false,
            }}
          >
            {Array.from({ length: loadPicAmount }).map((v, i) => {
              return (
                <SwiperSlide
                  key={i}
                  className="mb-3 px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <img
                    onClick={() => {
                      history.push(`/${brandUrl}/details/${c.carzillaId}`, {
                        scroll: true,
                      });
                    }}
                    className={`select-none rounded aspect-[1.333] w-full h-fit`}
                    alt={
                      'Bild des Neuwagens ' + c.model + ' der Firma ' + c.brand
                    }
                    loading={i >= 1 ? 'lazy' : 'eager'}
                    src={getApiUrl(
                      `/images/cars/${c.carzillaId}_${String(i + 1).padStart(
                        2,
                        '0'
                      )}.jpg`
                    )}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <div className="col-span-12 md:col-span-6 grid grid-cols-12 gap-2 my-4 items-start">
          {d.powerKw && (
            <div
              className="col-span-3 rounded  p-1 aspect-square flex flex-col justify-center items-center"
              title="Leistung in Kilowatt"
            >
              <span className="text-2xl text-primary">
                {Math.round(d.powerKw).toFixed(0)}
              </span>
              <span className="text-sm">kW</span>
            </div>
          )}
          {d.powerPS && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Leistung in PS"
            >
              <span className="text-2xl text-primary">
                {Math.round(d.powerPS).toFixed(0)}
              </span>
              <span className="text-sm">PS</span>
            </div>
          )}
          {d.firstRegistration && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Monat und Jahr der Erstzulassung"
            >
              <span className="text-2xl text-primary">
                {dayjs(d.firstRegistration).format('M/YY')}
              </span>
              <span className="text-sm">EZ</span>
            </div>
          )}
          {d.nextHU && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Monat und Jahr der nächsten Hauptuntersuchung"
            >
              <span className="text-2xl text-primary">
                {dayjs(d.nextHU).format('M/YY')}
              </span>
              <span className="text-sm">HU</span>
            </div>
          )}
          {d.seatCount && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Anzahl der Sitze"
            >
              <span className="text-2xl text-primary">{d.seatCount}</span>
              <span className="text-sm">Sitze</span>
            </div>
          )}
          {d.doorCount && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Anzahl der Türen"
            >
              <span className="text-2xl text-primary">{d.doorCount}</span>
              <span className="text-sm">Türen</span>
            </div>
          )}
          {d.previousOwners && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Anzahl der Vorbesitzer"
            >
              <span className="text-2xl text-primary">{d.previousOwners}</span>
              <span className="text-sm">Vorbesitzer</span>
            </div>
          )}
          {cons.co2Efficiency && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="CO2-Effizienzklasse"
            >
              <span className="text-2xl text-primary">
                {cons.co2Efficiency}
              </span>
              <span className="text-sm">Effizienz</span>
            </div>
          )}
          {cons.emissionClass && (
            <div
              className="col-span-3 rounded p-1 aspect-square flex flex-col justify-center items-center"
              title="Emissionsklasse"
            >
              <span className="text-2xl text-primary">
                {cons.emissionClass}
              </span>
              <span className="text-sm">Em.-Klasse</span>
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-6 grid grid-cols-12 gap-2 my-4 px-2">
          {d.kilometers && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Kilometerstand"
            >
              <FontAwesomeIcon icon={faRoad} className="mr-4" />
              <span className="text-lg">
                {Math.round(d.kilometers).toLocaleString('de-DE')} km
              </span>
            </div>
          )}
          {d.baseColor && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Grundfarbe"
            >
              <FontAwesomeIcon icon={faBrush} className="mr-4" />
              <span className="text-lg">{d.baseColor}</span>
            </div>
          )}
          {d.transmissionType && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Getriebe"
            >
              <FontAwesomeIcon icon={faGear} className="mr-4" />
              <span className="text-lg">{d.transmissionType}</span>
            </div>
          )}
          {d.fuelType && (
            <div
              className="col-span-6 rounded p-1 flex flex-row justify-start items-center"
              title="Treibstoff"
            >
              <FontAwesomeIcon icon={faDroplet} className="mr-4" />
              <span className="text-lg">{d.fuelType}</span>
            </div>
          )}
        </div>
        <div className="col-span-12 md:col-span-6 px-4 my-6 flex justify-end items-center">
          <PriceInfo
            total={props.priceOptions.includes('total') && d.price}
            financing={
              props.priceOptions.includes('financing') &&
              c.financingProposal.monthlyRate
            }
            priceFeature="financing"
            id={data.carzillaId}
            noAU={props.noAU}
          />
        </div>
        <div
          className="col-span-12 my-4 flex flex-wrap gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button onClick={props.showDetailPage}>Mehr Informationen</Button>
          <Button
            onClick={() =>
              history.push(`/${brandUrl}/details/${c.carzillaId}`, {
                scroll: true,
                data: {
                  reason: 'Probefahrt',
                },
              })
            }
          >
            Probefahrt
          </Button>
          <Button
            onClick={() =>
              history.push(`/${brandUrl}/details/${c.carzillaId}`, {
                scroll: true,
                data: {
                  reason: 'Beratung',
                },
              })
            }
          >
            Beratungstermin
          </Button>
          <Button
            icon={faHeart}
            color={compare ? '' : 'var(--text-color)'}
            className={`${classes.button}`}
            onClick={() => {
              compareCtx.toggleCars(c.carzillaId);
            }}
          >
            Vergleichen
          </Button>
        </div>
        <div className="col-span-12 leading-3">
          {consumptions.map((c, i) => {
            return (
              <span key={i} className="adminInfo">
                <span>{parse(c.case)}:</span>
                <span>{' ' + c.value}</span>
                <span>{i !== consumptions.length - 1 && ', '}</span>
              </span>
            );
          })}
        </div>
        <span className="col-span-12 adminInfo">(ID: {data.carzillaId})</span>
      </div>
    </Animation>
  );
}

export function CarPreviewSkeleton() {
  return (
    <div className="animate-pulse w-full text-left grid grid-cols-12 gap-4">
      {/* Überschrift + Herz-Icon */}
      <div className="relative col-span-12">
        <div className="h-6 bg-gray-300 rounded w-2/3 mb-1"></div>
        <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
        <div className="absolute top-0 right-0 w-6 h-6 bg-gray-300 rounded-full"></div>
      </div>

      {/* Swiper/Slider-Placeholder */}
      <div className="col-span-12 sm:col-span-6 md:col-span-12 flex justify-center">
        <div className="w-full h-64 bg-gray-300 rounded-xl" />
      </div>

      {/* Technische Kacheln (PS, EZ, Sitze etc.) */}
      <div className="col-span-12 sm:col-span-6 md:col-span-12 grid grid-cols-12 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="col-span-3 aspect-square bg-gray-300 rounded flex flex-col justify-center items-center"
          >
            <div className="h-6 w-10 bg-gray-200 rounded mb-1" />
            <div className="h-3 w-8 bg-gray-200 rounded" />
          </div>
        ))}

        {/* Weitere Fahrzeuginfos */}
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={`info-${i}`}
            className="col-span-6 flex items-center space-x-4"
          >
            <div className="w-5 h-5 bg-gray-300 rounded" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
        ))}
      </div>

      {/* Preisinfo */}
      <div className="col-span-12 flex justify-end">
        <div className="w-32 h-8 bg-gray-300 rounded" />
      </div>

      {/* Verbrauchswerte */}
      <div className="col-span-12 text-xs text-gray-400">
        <div className="h-3 bg-gray-200 w-full rounded mb-1"></div>
        <div className="h-3 bg-gray-200 w-full rounded mb-1"></div>
        <div className="h-3 bg-gray-200 w-full rounded mb-1"></div>
        <div className="h-3 bg-gray-200 w-full rounded mb-1"></div>
      </div>
    </div>
  );
}
